import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import { Loader2, Trophy, Target, Eye } from 'lucide-react'
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from 'recharts'

const TABS = ['Pareto contribution', 'Win / loss', 'Most opened'] as const
const th = 'px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap'
const td = 'px-3 py-2 text-sm text-gray-800 whitespace-nowrap'
const inp = 'rounded-md border border-gray-300 px-2 py-1 text-sm'
const pct = (v: any, d = 1) => (v == null ? '—' : `${(Number(v) * 100).toFixed(d)}%`)
const num = (v: any, d = 2) => (v == null ? '—' : Number(v).toFixed(d))
const sign = (v: any) => (v == null ? '' : Number(v) >= 0 ? 'text-emerald-600' : 'text-red-500')

export default function HFAnalytics() {
  const [tab, setTab] = useState<string>('Pareto contribution')
  const [months, setMonths] = useState(12)
  const [wlMonths, setWlMonths] = useState(36)
  const [days, setDays] = useState(90)

  const paretoQ = useQuery({
    queryKey: ['hf-pareto', months],
    queryFn: () => api.get('/hf-analytics/pareto', { params: { months } }).then(r => r.data),
  })
  const wlQ = useQuery({
    queryKey: ['hf-winloss', wlMonths],
    queryFn: () => api.get('/hf-analytics/win-loss', { params: { months: wlMonths } }).then(r => r.data),
    enabled: tab === 'Win / loss',
  })
  const opensQ = useQuery({
    queryKey: ['hf-opens', days],
    queryFn: () => api.get('/hf-analytics/opens/top', { params: { days, limit: 15 } }).then(r => r.data),
    enabled: tab === 'Most opened',
  })

  const p = paretoQ.data
  const chartData = (p?.rows || []).map((r: any) => ({
    name: String(r.fund_name || '').slice(0, 18),
    contribution: r.contribution * 100,
    cumulative: r.cumulative_gain_pct,
  }))

  return (
    <div className="space-y-4 p-1">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Performance Analytics</h1>
          <p className="text-sm text-gray-500">
            Which holdings drive the book, how reliably they win, and where attention is going.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-5 border-b border-gray-200 text-sm">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`-mb-px border-b-2 pb-2 pt-1 ${tab === t ? 'border-blue-600 font-medium text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>{t}</button>
        ))}
      </div>

      {/* ----------------------------------------------- Pareto */}
      {tab === 'Pareto contribution' && (
        <div className="space-y-4">
          <div className="flex items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
            <label className="text-sm">Window (months)
              <input className={`${inp} ml-1 w-20`} type="number" value={months} onChange={e => setMonths(Number(e.target.value))} />
            </label>
          </div>
          {paretoQ.isFetching && <Spin label="Computing contributions…" />}
          {p?.available === false && <Warn text={p.reason} />}
          {p?.available && (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Kpi label="Holdings" value={p.n_holdings} icon={<Trophy size={14} />} />
                <Kpi label="Contributors" value={p.n_contributors} cls="text-emerald-600" />
                <Kpi label="Detractors" value={p.n_detractors} cls="text-red-500" />
                <Kpi label="Names for 80% of gains" value={p.funds_for_80pct_of_gains ?? '—'} />
              </div>
              {p.concentration_note && (
                <div className="rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-800">{p.concentration_note}.</div>
              )}
              <Panel title="Contribution to P&L (bars) and cumulative share of gains (line)">
                <ResponsiveContainer width="100%" height={380}>
                  <ComposedChart data={chartData} margin={{ top: 10, right: 20, bottom: 60, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" interval={0} height={80} />
                    <YAxis yAxisId="l" tick={{ fontSize: 11 }} unit="%" />
                    <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                    <Tooltip formatter={(v: number, n: string) => [`${Number(v).toFixed(2)}%`, n]} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <ReferenceLine yAxisId="r" y={80} stroke="#94a3b8" strokeDasharray="4 4" label={{ value: '80%', fontSize: 10, position: 'right' }} />
                    <Bar yAxisId="l" dataKey="contribution" name="Contribution" fill="#2563eb" />
                    <Line yAxisId="r" type="monotone" dataKey="cumulative" name="Cumulative % of gains" stroke="#059669" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                  </ComposedChart>
                </ResponsiveContainer>
              </Panel>
              <Panel title="Holdings by contribution">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead><tr>{['Fund', 'Weight', 'Period return', 'Contribution', 'Cumulative % of gains', 'Months'].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {(p.rows || []).map((r: any) => (
                        <tr key={r.fund_id} className="hover:bg-gray-50">
                          <td className={`${td} font-medium`}>{r.fund_name}</td>
                          <td className={td}>{pct(r.weight)}</td>
                          <td className={`${td} ${sign(r.period_return)}`}>{pct(r.period_return)}</td>
                          <td className={`${td} font-medium ${sign(r.contribution)}`}>{pct(r.contribution, 2)}</td>
                          <td className={td}>{r.cumulative_gain_pct == null ? '—' : `${r.cumulative_gain_pct.toFixed(1)}%`}</td>
                          <td className={td}>{r.n_months}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ----------------------------------------------- Win / loss */}
      {tab === 'Win / loss' && (
        <div className="space-y-4">
          <div className="flex items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
            <label className="text-sm">Window (months)
              <input className={`${inp} ml-1 w-20`} type="number" value={wlMonths} onChange={e => setWlMonths(Number(e.target.value))} />
            </label>
          </div>
          {wlQ.isFetching && <Spin label="Computing win/loss…" />}
          {wlQ.data?.available === false && <Warn text={wlQ.data.reason} />}
          {wlQ.data?.available && (
            <>
              {wlQ.data.portfolio && (
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                  <Kpi label="Portfolio win rate" value={pct(wlQ.data.portfolio.win_rate)} icon={<Target size={14} />} />
                  <Kpi label="Avg win" value={pct(wlQ.data.portfolio.avg_win, 2)} cls="text-emerald-600" />
                  <Kpi label="Avg loss" value={pct(wlQ.data.portfolio.avg_loss, 2)} cls="text-red-500" />
                  <Kpi label="Payoff ratio" value={num(wlQ.data.portfolio.payoff_ratio)} />
                  <Kpi label="Worst month" value={pct(wlQ.data.portfolio.worst_month, 2)} cls="text-red-500" />
                </div>
              )}
              <Panel title="Per-holding win/loss profile">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead><tr>{['Fund', 'Months', 'Win rate', 'Wins', 'Losses', 'Avg win', 'Avg loss', 'Payoff', 'Expectancy', 'Best', 'Worst'].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {(wlQ.data.rows || []).map((r: any) => (
                        <tr key={r.fund_id} className="hover:bg-gray-50">
                          <td className={`${td} font-medium`}>{r.fund_name}</td>
                          <td className={td}>{r.n_months}</td>
                          <td className={`${td} font-medium ${r.win_rate >= 0.5 ? 'text-emerald-600' : 'text-red-500'}`}>{pct(r.win_rate, 0)}</td>
                          <td className={td}>{r.n_wins}</td><td className={td}>{r.n_losses}</td>
                          <td className={`${td} text-emerald-600`}>{pct(r.avg_win, 2)}</td>
                          <td className={`${td} text-red-500`}>{pct(r.avg_loss, 2)}</td>
                          <td className={td}>{num(r.payoff_ratio)}</td>
                          <td className={`${td} ${sign(r.expectancy)}`}>{pct(r.expectancy, 2)}</td>
                          <td className={`${td} text-emerald-600`}>{pct(r.best_month, 2)}</td>
                          <td className={`${td} text-red-500`}>{pct(r.worst_month, 2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ----------------------------------------------- Most opened */}
      {tab === 'Most opened' && (
        <div className="space-y-4">
          <div className="flex items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
            <label className="text-sm">Window (days)
              <input className={`${inp} ml-1 w-20`} type="number" value={days} onChange={e => setDays(Number(e.target.value))} />
            </label>
          </div>
          {opensQ.isFetching && <Spin label="Loading…" />}
          <Panel title="Most frequently opened funds">
            <p className="mb-2 text-xs text-gray-500">
              Recorded when a fund page is opened — the desk's revealed attention, independent of performance.
            </p>
            <table className="min-w-full divide-y divide-gray-200">
              <thead><tr>{['Fund', 'Opens', 'Users', 'Last opened'].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-gray-100">
                {(opensQ.data?.rows || []).map((r: any) => (
                  <tr key={r.fund_id} className="hover:bg-gray-50">
                    <td className={`${td} font-medium`}>{r.fund_name || `#${r.fund_id}`}</td>
                    <td className={td}>{r.opens}</td>
                    <td className={td}>{r.users}</td>
                    <td className={td}>{String(r.last_opened).slice(0, 16)}</td>
                  </tr>
                ))}
                {!(opensQ.data?.rows || []).length && (
                  <tr><td colSpan={4} className="px-3 py-8 text-center text-sm text-gray-400">
                    <Eye className="mx-auto mb-2 text-gray-300" size={20} />
                    No opens recorded yet in this window.
                  </td></tr>
                )}
              </tbody>
            </table>
          </Panel>
        </div>
      )}
    </div>
  )
}

function Kpi({ label, value, cls, icon }: { label: string; value: any; cls?: string; icon?: any }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-center gap-1 text-xs text-gray-400">{icon}{label}</div>
      <div className={`mt-1 text-lg font-semibold ${cls || 'text-gray-900'}`}>{value ?? '—'}</div>
    </div>
  )
}
function Panel({ title, children }: { title: string; children: any }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-2 text-sm font-medium text-gray-700">{title}</div>
      {children}
    </div>
  )
}
function Spin({ label }: { label?: string }) {
  return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="animate-spin" size={15} />{label}</div>
}
function Warn({ text }: { text?: string }) {
  return <div className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-700">{text || 'Unavailable'}</div>
}
